/**
 * Typed wrappers for the citizen self-service address-book endpoints on
 * service-customer (port 3040 by default).
 *
 * Endpoints: GET/POST /v1/customers/me/addresses
 *            PUT/DELETE /v1/customers/me/addresses/:id
 *
 * RBAC: each endpoint is gated by `Address/{read,create,update,delete}-own`.
 * The `customer` role seeds these permissions at self-registration. Citizens
 * carry these permissions in their JWT so the MatrixRbacGuard passes.
 *
 * Ownership: the backend resolves the customer from the JWT's `upmp_user_id`
 * claim — no customerId is required in the URL path.
 *
 * Mirrors `apps/service-customer/src/modules/customer/customer.dto.ts`.
 */
import {
  requestJson,
  type RequestJsonOptions,
} from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export type AddressLabel = 'HOME' | 'WORK' | 'OTHER';

export interface CustomerAddressBody {
  readonly label?: AddressLabel;
  readonly recipientName?: string;
  readonly line1: string;
  readonly line2?: string;
  readonly landmark?: string;
  readonly city: string;
  readonly state: string;
  readonly postalCode?: string;
  readonly countryCode?: string;
  readonly latitude?: string;
  readonly longitude?: string;
  readonly contactPhone?: string;
  readonly isPrimary?: boolean;
  // service-geo soft-FK columns. All optional — only the cascading
  // <AddressFields> picker populates them today; legacy callers omit and the
  // backend stores NULL.
  readonly geoCountryId?: number;
  readonly geoCountryName?: string;
  readonly geoStateId?: number;
  readonly geoStateName?: string;
  readonly geoLgaId?: number;
  readonly geoLgaName?: string;
  readonly geoWardId?: number;
  readonly geoWardName?: string;
}

export interface CustomerAddressResponse extends CustomerAddressBody {
  readonly id: string;
  readonly customerId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

/** GET /v1/customers/me/addresses — list the authenticated citizen's addresses. */
export async function listAddresses(
  options: CallOpts = {},
): Promise<readonly CustomerAddressResponse[]> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<readonly CustomerAddressResponse[]>({
    method: 'GET',
    baseUrl: env.serviceCustomerBaseUrl,
    path: '/v1/customers/me/addresses',
    bearerToken: bearerOf(options),
    ...rest,
  });
}

/** POST /v1/customers/me/addresses — add an address to the citizen's address book. */
export async function createAddress(
  body: CustomerAddressBody,
  options: CallOpts = {},
): Promise<CustomerAddressResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<CustomerAddressResponse>({
    method: 'POST',
    baseUrl: env.serviceCustomerBaseUrl,
    path: '/v1/customers/me/addresses',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

/** PUT /v1/customers/me/addresses/:id — update one of the citizen's addresses. */
export async function updateAddress(
  addressId: string,
  body: Partial<CustomerAddressBody>,
  options: CallOpts = {},
): Promise<CustomerAddressResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<CustomerAddressResponse>({
    method: 'PUT',
    baseUrl: env.serviceCustomerBaseUrl,
    path: `/v1/customers/me/addresses/${encodeURIComponent(addressId)}`,
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

/** DELETE /v1/customers/me/addresses/:id — remove one of the citizen's addresses. */
export async function deleteAddress(
  addressId: string,
  options: CallOpts = {},
): Promise<CustomerAddressResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<CustomerAddressResponse>({
    method: 'DELETE',
    baseUrl: env.serviceCustomerBaseUrl,
    path: `/v1/customers/me/addresses/${encodeURIComponent(addressId)}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}
